import { EquipmentTimersDurationByProductListChart } from '@frontend/chart/components';
import { formatTimeDifference } from '@frontend/common';
import { type ReactNode, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { StatisticsCard } from '../../shared';
import {
    ProductAverageDurationItem,
    useLast30DaysLendingAverageUseDurationByProductListCard
} from './use-last-30-days-lending-average-use-duration-by-product-list-card';

export interface Last30DaysLendingAverageUseDurationByProductListCardProps {
    accountId: string;
}

type AverageDurationRow = { type: 'section'; key: string; label: ReactNode } | { type: 'item'; item: ProductAverageDurationItem; tone: 'warning' | 'success' };

const formatAverageDuration = (averageSeconds: number): string => {
    const end = Date.now();
    return formatTimeDifference(new Date(end - averageSeconds * 1000).toISOString(), new Date(end).toISOString());
};

export const Last30DaysLendingAverageUseDurationByProductListCard = (props: Last30DaysLendingAverageUseDurationByProductListCardProps) => {
    const { formatMessage } = useIntl();

    const unknownLabel = formatMessage({
        id: 'Objects.UNKNOWN.DEFAULT',
        description: 'Label for unknown',
        defaultMessage: 'Unknown'
    });

    const { highestItems, lowestItems, isLoading } = useLast30DaysLendingAverageUseDurationByProductListCard({
        ...props,
        unknownLabel
    });

    const equipmentHeader = formatMessage({
        id: 'Objects.EQUIPMENT.DEFAULT',
        description: 'Label for equipment',
        defaultMessage: 'Equipment'
    });

    const durationHeader = formatMessage({
        id: 'platform.statistics.lending.last-30-days-lending-average-use-duration-by-product-list-card.avg-duration',
        description: 'Column header for average lend duration on the lending dashboard average duration list',
        defaultMessage: 'Avg duration'
    });

    const rows = useMemo((): AverageDurationRow[] => {
        const result: AverageDurationRow[] = [];

        if (highestItems.length > 0) {
            result.push({
                type: 'section',
                key: 'highest',
                label: (
                    <FormattedMessage
                        id='platform.statistics.lending.last-30-days-lending-average-use-duration-by-product-list-card.highest'
                        description='Section heading above equipments with the longest average lend duration on the lending dashboard'
                        defaultMessage='Highest'
                    />
                )
            });
            result.push(...highestItems.map((item) => ({ type: 'item' as const, item, tone: 'warning' as const })));
        }

        if (lowestItems.length > 0) {
            result.push({
                type: 'section',
                key: 'lowest',
                label: (
                    <FormattedMessage
                        id='platform.statistics.lending.last-30-days-lending-average-use-duration-by-product-list-card.lowest'
                        description='Section heading above equipments with the shortest average lend duration on the lending dashboard'
                        defaultMessage='Lowest'
                    />
                )
            });
            result.push(...lowestItems.map((item) => ({ type: 'item' as const, item, tone: 'success' as const })));
        }

        return result;
    }, [highestItems, lowestItems]);

    return (
        <StatisticsCard
            chartKey={EquipmentTimersDurationByProductListChart.definition.key}
            title={
                <FormattedMessage
                    id='platform.statistics.lending.last-30-days-lending-average-use-duration-by-product-list-card.title'
                    description='Title of the lending dashboard list showing equipments with the highest and lowest average completed lend duration over the last 30 days'
                    defaultMessage='Average duration in use'
                />
            }
            subtitle={
                <FormattedMessage
                    id='widget.last-30-days'
                    defaultMessage='last 30 days'
                />
            }
            isLoading={isLoading}
            hasData={rows.length > 0}>
            <div className='min-h-0 flex-1 overflow-y-auto'>
                <div className='border-border-default text-foreground-default flex items-start gap-4 border-b text-base font-medium'>
                    <div className='w-full px-4 py-3'>{equipmentHeader}</div>
                    <div className='w-[140px] shrink-0 px-4 py-3 text-right'>{durationHeader}</div>
                </div>
                {rows.map((row) =>
                    row.type === 'section' ? (
                        <div
                            key={row.key}
                            className='border-border-default text-foreground-default border-b px-4 py-3 text-base font-semibold'>
                            {row.label}
                        </div>
                    ) : (
                        <div
                            key={row.item.key}
                            className='border-border-default flex h-11 items-center gap-4 border-b'>
                            <div className='text-foreground-default flex w-full min-w-0 items-center px-4 text-sm font-medium'>{row.item.name}</div>
                            <div className='flex w-[140px] shrink-0 justify-end pe-4'>
                                <span
                                    className={`text-background-surface inline-flex min-w-8 items-center justify-center rounded-full px-2 py-0.5 text-sm font-semibold ${
                                        row.tone === 'warning' ? 'bg-status-warning' : 'bg-status-success'
                                    }`}>
                                    {formatAverageDuration(row.item.averageSeconds)}
                                </span>
                            </div>
                        </div>
                    )
                )}
            </div>
        </StatisticsCard>
    );
};
